// client/src/lib/api/customers-api.ts
// ===========================================
// MÓDULO DE GESTIÓN DE CLIENTES
// Maneja la obtención, creación, actualización, eliminación y búsqueda de clientes.
// Usa apiRequest para inyectar automáticamente headers y manejar errores.
// ===========================================

import { apiRequest, buildUrlWithParams, ApiError } from "./api-client";

// -------------------------------------------
// Obtener lista de clientes de una tienda
// -------------------------------------------
export async function getShopCustomers(shopId: string): Promise<any[]> {
  const response = await apiRequest<{ customers: any[] }>(
    `/api/shops/${shopId}/customers`,
  );
  return response.customers;
}

// -------------------------------------------
// Obtener clientes con estadísticas
// -------------------------------------------
export async function getCustomersWithStats(shopId: string): Promise<any[]> {
  const response = await apiRequest<{ customers: any[] }>(
    `/api/shops/${shopId}/customers-with-stats`,
  );
  return response.customers;
}

// -------------------------------------------
// Obtener cliente por CI
// -------------------------------------------
export async function getCustomerByCI(ci: string): Promise<any> {
  const response = await apiRequest<{ customer: any }>(
    `/api/customers/ci/${ci}`,
  );
  return response.customer;
}

// -------------------------------------------
// Obtener cliente por teléfono
// -------------------------------------------
export async function getCustomerByPhone(phone: string): Promise<any> {
  const response = await apiRequest<{ customer: any }>(
    `/api/customers/phone/${phone}`,
  );
  return response.customer;
}

// -------------------------------------------
// Obtener cliente por teléfono para una tienda específica
// -------------------------------------------
// COMENTARIO: Mantenemos la lógica de probar varios formatos de teléfono
// (ej: con prefijo 53, +53, etc.) ya que esto depende de cómo los guarde el usuario.
// Usamos ApiError para verificar si el código de estado es 404 y continuar con el siguiente formato.
export async function getCustomerByPhoneAndShop(
  shopId: string,
  phone: string,
): Promise<any> {
  // Limpiar el teléfono de caracteres no numéricos
  const cleanPhone = phone.replace(/[\s\-\(\)\+]/g, "");

  const possibleFormats = [
    cleanPhone, // 54882577
    `+53${cleanPhone}`, // +5354882577
    `53${cleanPhone}`, // 5354882577
    `0${cleanPhone}`, // 054882577
  ];

  let lastError: any = null;

  for (const format of possibleFormats) {
    try {
      const response = await apiRequest<{ customer: any }>(
        `/api/shops/${shopId}/customers/phone/${encodeURIComponent(format)}`,
      );
      return response.customer;
    } catch (error: any) {
      // Si es 404, el cliente no existe con ese formato, probamos el siguiente
      if (error instanceof ApiError && error.status === 404) {
        continue;
      }
      // Guardamos el error por si todos los intentos fallan
      lastError = error;
    }
  }

  // Si terminamos el bucle sin éxito, lanzamos el último error o uno genérico
  throw lastError || new Error("Cliente no encontrado en esta tienda");
}

// -------------------------------------------
// Buscar clientes
// -------------------------------------------
export async function searchCustomers(
  shopId: string,
  query: string,
): Promise<any[]> {
  const url = buildUrlWithParams(`/api/shops/${shopId}/customers/search`, {
    q: query,
  });
  const response = await apiRequest<{ customers: any[] }>(url);
  return response.customers;
}

// -------------------------------------------
// Crear cliente
// -------------------------------------------
export async function createCustomer(customerData: {
  shopId: string;
  fullName: string;
  ci: string;
  phone: string;
  email?: string;
  address?: string;
  notes?: string;
}): Promise<any> {
  const response = await apiRequest<{ customer: any }>("/api/customers", {
    method: "POST",
    body: JSON.stringify(customerData),
  });
  return response.customer;
}

// -------------------------------------------
// Actualizar cliente
// -------------------------------------------
export async function updateCustomerForShop(
  shopId: string,
  customerId: string,
  updates: {
    fullName?: string;
    phone?: string;
    email?: string;
    address?: string;
    notes?: string;
    isActive?: boolean;
  },
): Promise<any> {
  const response = await apiRequest<{ customer: any }>(
    `/api/shops/${shopId}/customers/${customerId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
  );
  return response.customer;
}

// -------------------------------------------
// Eliminar cliente
// -------------------------------------------
export async function deleteCustomerForShop(
  shopId: string,
  customerId: string,
): Promise<void> {
  return apiRequest<void>(`/api/shops/${shopId}/customers/${customerId}`, {
    method: "DELETE",
  });
}
